/**
 * Documentation Page
 *
 * Comprehensive documentation with sidebar TOC, grouped sections,
 * command search, and consistent MUI styling.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  SmartToy as BotIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  Chat as ChatIcon,
  Cached as CachedIcon,
  IntegrationInstructions as IntegrationIcon,
  Analytics as AnalyticsIcon,
  AdminPanelSettings as AdminIcon,
  NewReleases as ChangelogIcon,
  GitHub as GitHubIcon,
  Terminal as TerminalIcon,
  Forum as ForumIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

// ── Command Data ──────────────────────────────────────────────────────
interface Command {
  command: string;
  description: string;
  role?: string;
}

const coreCommands: Command[] = [
  { command: '!help', description: 'Show available commands and usage' },
  { command: '!status', description: 'Show thread status and linked JIRA tickets' },
  { command: '!ask <question>', description: 'Ask a follow-up question in the thread' },
  { command: '!docs [topic]', description: 'Search or list team documentation' },
  { command: '!summary', description: 'Generate an AI summary of the current thread' },
  { command: '!create [P0-P3]', description: 'Create a JIRA ticket from the thread (optional priority)' },
  { command: '!assign @user', description: 'Assign the issue to a team expert' },
  { command: '!link JIRA-123', description: 'Link the current thread to an existing JIRA ticket' },
  { command: '!bad [feedback]', description: "Flag the bot's last response as incorrect with optional feedback" },
  { command: '!reset', description: 'Clear thread context and start fresh' },
  { command: '!escalate [team]', description: 'Escalate issue to the responsible team (auto-detects by keywords)', role: 'Team Lead' },
  { command: '!resolve', description: 'Mark the issue as resolved', role: 'Team Lead' },
  { command: '!reopen', description: 'Reopen a resolved issue', role: 'Team Lead' },
  { command: '!priority P0-P3', description: 'Change ticket priority level', role: 'Team Lead' },
];

const jiraCreateCommands: Command[] = [
  { command: '!jira create <type> <title>', description: 'Create a ticket with specific type (Task/Story/Bug/Epic/Initiative) and title' },
  { command: '!jira create <type>', description: 'Create a ticket with specific type. AI generates title and fills template from thread context' },
  { command: '!jira create <title>', description: 'Create a Task with the specified title' },
  { command: '!jira create', description: 'Create a Task with AI-generated content from conversation context' },
  { command: '!jira accept', description: 'Accept a pending draft and create the JIRA ticket' },
  { command: '!jira reject <feedback>', description: 'Reject the draft and regenerate with your feedback' },
  { command: '!jira bulk <type> <t1>; <t2>; ...', description: 'Create multiple tickets at once, separated by semicolons' },
  { command: '!jira link <issue> <parent>', description: 'Link an issue to an epic, or an epic to an initiative' },
];

const jiraSearchCommands: Command[] = [
  { command: '!jira search <query>', description: 'Search for tickets using natural language (e.g., "open bugs about auth")' },
  { command: '!jira filter open', description: 'Show all open tickets' },
  { command: '!jira filter in-progress', description: 'Show in-progress tickets' },
  { command: '!jira filter blocked', description: 'Show blocked tickets' },
  { command: '!jira filter recent', description: 'Show recently updated tickets' },
  { command: '!jira filter high-priority', description: 'Show high priority tickets' },
  { command: '!jira filter save <name> <jql>', description: 'Save a custom JQL filter (use {project} as placeholder)' },
  { command: '!jira filter list', description: 'List all saved custom filters' },
  { command: '!jira filter delete <name>', description: 'Delete a saved filter' },
  { command: '!jira my tickets [email]', description: 'Show tickets assigned to you, or for a specific email address' },
  { command: '!jira stale', description: 'Show tickets not updated in 7+ days' },
  { command: '!jira overdue', description: 'Show tickets past their due date' },
];

const jiraHierarchyCommands: Command[] = [
  { command: '!jira epic <key>', description: 'List all tickets under an epic (e.g., !jira epic PROJ-100)' },
  { command: '!jira initiatives', description: 'Show full project hierarchy: Initiatives \u2192 Epics \u2192 Stories/Tasks' },
];

const jiraImportCommands: Command[] = [
  { command: '!jira import-github <url> [type]', description: 'Import a single GitHub issue to JIRA' },
  { command: '!jira import-github bulk <repo> [type]', description: 'Import all open issues from a GitHub repository' },
  { command: '!jira stats', description: 'Show project statistics (open/closed counts, by status, by priority)' },
  { command: '!jira help', description: 'Show JIRA command help' },
];

const githubCommands: Command[] = [
  { command: '!github link', description: 'Bulk link GitHub issues to JIRA tickets. Each line: <github_url> <JIRA-KEY>' },
  { command: '!github help', description: 'Show GitHub command help' },
];

const agentCommands: Command[] = [
  { command: '!agent <task>', description: 'Create a new agent task (full lifecycle: plan → approve → implement → PR)' },
  { command: '!agent ask <question>', description: 'Ask a question or request a review — direct answer, no planning or PR' },
  { command: '!agent confirm <id> <repo#>', description: 'Pick a repo when the task description was ambiguous (uses the live default branch)' },
  { command: '!agent confirm <id> <repo#> <branch>', description: 'Pick a repo and specify a custom branch' },
  { command: '!agent approve <id>', description: 'Approve the implementation plan and start execution' },
  { command: '!agent reject <id> <feedback>', description: 'Reject plan with revision notes for the agent to retry' },
  { command: '!agent queue', description: 'Show the task queue with statuses (also: !agent status)' },
  { command: '!agent cancel <id>', description: 'Cancel a running or pending task' },
  { command: '!agent help', description: 'Show agent command help' },
];

// ── TOC Structure ─────────────────────────────────────────────────────
interface TocSection {
  id: string;
  label: string;
  group: string;
  link?: string; // External route instead of scroll
}

const tocSections: TocSection[] = [
  { id: 'overview', label: 'Overview', group: 'Getting Started' },
  { id: 'setup', label: 'Team Setup Guide', group: 'Getting Started' },
  { id: 'how-it-works', label: 'How It Works', group: 'Usage' },
  { id: 'features', label: 'Features', group: 'Usage' },
  { id: 'core-commands', label: 'Core Commands', group: 'Commands' },
  { id: 'jira-commands', label: 'JIRA Commands', group: 'Commands' },
  { id: 'github-commands', label: 'GitHub Commands', group: 'Commands' },
  { id: 'agent-commands', label: 'Agent Commands', group: 'Commands' },
  { id: 'team-settings', label: 'Team Settings', group: 'Admin' },
  { id: 'admin-features', label: 'Admin Features', group: 'Admin' },
  { id: 'changelog', label: 'Changelog', group: 'Admin', link: '/changelog' },
];

// ── Styled code helper ────────────────────────────────────────────────
const codeSx = {
  backgroundColor: 'action.hover',
  px: 0.75,
  py: 0.25,
  borderRadius: 0.5,
  fontFamily: 'monospace',
  fontSize: '0.85em',
  display: 'inline-block',
};

const TOC_WIDTH = 200;

export default function DocumentationPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [activeSection, setActiveSection] = useState('overview');
  const [cmdFilter, setCmdFilter] = useState('');
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  // ── IntersectionObserver for active section tracking ──────────────
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 },
    );

    for (const section of tocSections) {
      const el = sectionRefs.current[section.id];
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const setRef = (id: string) => (el: HTMLElement | null) => {
    sectionRefs.current[id] = el;
  };

  // ── Command filter logic ──────────────────────────────────────────
  const lowerFilter = cmdFilter.toLowerCase();
  const filterCmds = (cmds: Command[]) =>
    cmds.filter(
      (c) =>
        c.command.toLowerCase().includes(lowerFilter) ||
        c.description.toLowerCase().includes(lowerFilter),
    );

  /* eslint-disable react-hooks/exhaustive-deps */
  const filteredCore = useMemo(() => filterCmds(coreCommands), [lowerFilter]);
  const filteredJiraCreate = useMemo(() => filterCmds(jiraCreateCommands), [lowerFilter]);
  const filteredJiraSearch = useMemo(() => filterCmds(jiraSearchCommands), [lowerFilter]);
  const filteredJiraHierarchy = useMemo(() => filterCmds(jiraHierarchyCommands), [lowerFilter]);
  const filteredJiraImport = useMemo(() => filterCmds(jiraImportCommands), [lowerFilter]);
  const filteredGithub = useMemo(() => filterCmds(githubCommands), [lowerFilter]);
  const filteredAgent = useMemo(() => filterCmds(agentCommands), [lowerFilter]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // ── Render command table ──────────────────────────────────────────
  const renderCommandTable = (commands: Command[], showRole = false) => {
    if (commands.length === 0) return null;
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: showRole ? '30%' : '35%' }}>Command</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              {showRole && <TableCell sx={{ fontWeight: 600, width: '10%' }}>Role</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {commands.map((cmd) => (
              <TableRow key={cmd.command}>
                <TableCell>
                  <Box component="code" sx={codeSx}>{cmd.command}</Box>
                </TableCell>
                <TableCell>{cmd.description}</TableCell>
                {showRole && (
                  <TableCell>
                    {cmd.role && <Chip label={cmd.role} size="small" variant="outlined" />}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ── Category header ───────────────────────────────────────────────
  const renderCategoryHeader = (label: string) => (
    <Typography
      variant="overline"
      sx={{ color: 'text.secondary', letterSpacing: 1.5, mt: 4, mb: 1, display: 'block' }}
    >
      {label}
    </Typography>
  );

  // ── Numbered step ─────────────────────────────────────────────────
  const renderStep = (step: number, title: string, desc: string) => (
    <Box key={step} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box
        sx={{
          width: 28, height: 28, borderRadius: '50%', backgroundColor: 'primary.main',
          color: 'primary.contrastText', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0, mt: 0.25,
        }}
      >
        {step}
      </Box>
      <Box>
        <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{desc}</Typography>
      </Box>
    </Box>
  );

  // ── TOC ───────────────────────────────────────────────────────────
  const toc = (
    <Box
      sx={{
        width: TOC_WIDTH, flexShrink: 0, position: 'sticky', top: 80,
        alignSelf: 'flex-start', maxHeight: 'calc(100vh - 100px)', overflowY: 'auto', pr: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
        On this page
      </Typography>
      <List dense disablePadding>
        {(() => {
          let lastGroup = '';
          return tocSections.map((section) => {
            const showGroup = section.group !== lastGroup;
            lastGroup = section.group;
            return (
              <Box key={section.id}>
                {showGroup && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.disabled', pl: 1, pt: 1.5, pb: 0.5, display: 'block', fontWeight: 600 }}
                  >
                    {section.group}
                  </Typography>
                )}
                <ListItemButton
                  selected={!section.link && activeSection === section.id}
                  onClick={() => section.link ? navigate(section.link) : scrollTo(section.id)}
                  sx={{
                    py: 0.5, px: 1, borderRadius: 1, minHeight: 32,
                    '&.Mui-selected': { borderLeft: 2, borderColor: 'primary.main' },
                  }}
                >
                  <ListItemText
                    primary={section.label}
                    primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: activeSection === section.id ? 600 : 400 }}
                  />
                </ListItemButton>
              </Box>
            );
          });
        })()}
      </List>
    </Box>
  );

  // ── No-results message ────────────────────────────────────────────
  const noResults = cmdFilter && (
    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
      No commands matching &quot;{cmdFilter}&quot;
    </Typography>
  );

  // ── Main content ──────────────────────────────────────────────────
  const content = (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {/* ─── Getting Started ─────────────────────────────────────── */}
      {renderCategoryHeader('Getting Started')}

      <Box id="overview" ref={setRef('overview')} sx={{ scrollMarginTop: 80, mb: 4 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BotIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" fontWeight={600}>Welcome to AI Wingman</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              AI Wingman is an intelligent support assistant for Google Chat. It provides AI-powered
              responses, expert routing, response caching, JIRA &amp; GitHub integration, and an agentic
              coding assistant — all configurable per team.
            </Typography>
            <Alert severity="info">
              Most advanced features (JIRA commands, GitHub linking, Agent mode) are disabled by default.
              Team leads can enable them in the <strong>AI &amp; Prompting</strong> tab under Feature Settings.
            </Alert>
          </CardContent>
        </Card>
        <Grid container spacing={2}>
          {[
            { icon: <SpeedIcon color="success" />, title: 'Faster Response Times', desc: 'Instantly answer common questions without waiting for team members' },
            { icon: <GroupIcon color="primary" />, title: 'Expert Routing', desc: 'Automatically suggest the right team member based on topic keywords' },
            { icon: <CachedIcon color="info" />, title: 'Knowledge Retention', desc: 'Cache successful responses for consistent, instant recurring answers' },
            { icon: <AnalyticsIcon color="secondary" />, title: 'Analytics & Insights', desc: 'Track query patterns, response times, cache hits, and team performance' },
          ].map((item) => (
            <Grid item xs={12} sm={6} key={item.title}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                {item.icon}
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box id="setup" ref={setRef('setup')} sx={{ scrollMarginTop: 80, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Team Setup Guide</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {renderStep(1, 'Create Team', 'Go to My Teams → Create Team. Enter your team name, description, and Google Chat Space ID.')}
          {renderStep(2, 'Add Team Members', 'Import members via JSON or add them individually. Assign roles: admin, team_lead, or member.')}
          {renderStep(3, 'Add Documentation Links', "Link to your team's Confluence pages, GitHub wikis, or external docs. The bot uses these for context.")}
          {renderStep(4, 'Add Repositories', 'Connect your GitHub repositories so the bot can reference code and README files.')}
          {renderStep(5, 'Define Experts', 'Add team experts with their areas of expertise and keywords. The bot will @-mention them for relevant queries.')}
          {renderStep(6, 'Set Up Responsibility Matrix', 'Define which components/services your team owns and escalation paths to other teams.')}
          {renderStep(7, 'Configure AI & Features', 'Choose your AI model, adjust temperature/tokens, and enable optional features (JIRA, GitHub, Agent).')}
          {renderStep(8, 'Submit for Approval', 'Submit your team for admin review. Once approved, the bot starts responding in your Google Chat space.')}
        </Box>
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> You can upload members, experts, docs, and repos in bulk using JSON files.
            Download sample files from the onboarding wizard.
          </Typography>
        </Alert>
      </Box>

      {/* ─── Usage ───────────────────────────────────────────────── */}
      {renderCategoryHeader('Usage')}

      <Box id="how-it-works" ref={setRef('how-it-works')} sx={{ scrollMarginTop: 80, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>How It Works</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          When someone @-mentions the bot or sends a direct message in your Google Chat space,
          AI Wingman processes it through this pipeline:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {renderStep(1, 'Message Detection', 'The bot monitors your Google Chat space for @mentions, direct messages, and thread replies.')}
          {renderStep(2, 'Command Check', 'If the message starts with ! (e.g., !help, !jira create), it\'s routed directly to the command handler.')}
          {renderStep(3, 'Cache Check', 'For regular queries, the system checks if a similar question has been answered before (if caching is enabled).')}
          {renderStep(4, 'Context Gathering', 'The bot fetches relevant context: team docs, repo READMEs, JIRA tickets, GitHub issues, and thread history.')}
          {renderStep(5, 'AI Processing', 'The query + context is processed by your configured AI model (Claude, Llama, etc.) with team-specific prompting.')}
          {renderStep(6, 'Expert Matching', 'Based on keywords in the query, the system identifies and @-mentions relevant team experts.')}
          {renderStep(7, 'Response Delivery', 'A formatted response is sent back to the same Google Chat thread with the answer and expert suggestions.')}
        </Box>
      </Box>

      <Box id="features" ref={setRef('features')} sx={{ scrollMarginTop: 80, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Features & Capabilities</Typography>
        <Grid container spacing={2}>
          {[
            { icon: <BotIcon sx={{ color: 'primary.main' }} />, title: 'AI-Powered Responses', desc: 'Configurable AI models (Claude, Llama, etc.) with adjustable temperature, max tokens, and custom prompting per team.' },
            { icon: <CachedIcon sx={{ color: 'info.main' }} />, title: 'Response Caching', desc: 'Similar queries are matched against cached responses. Admins can review, approve, and edit cached answers.' },
            { icon: <GroupIcon sx={{ color: 'success.main' }} />, title: 'Expert Matching', desc: 'Automatically @-mentions team experts based on their expertise keywords matching the query.' },
            { icon: <IntegrationIcon sx={{ color: 'warning.main' }} />, title: 'JIRA Integration', desc: 'Create tickets with type-specific templates, search with natural language, manage filters, and track hierarchy.' },
            { icon: <GitHubIcon sx={{ color: 'text.primary' }} />, title: 'GitHub Integration', desc: 'Auto-fetch GitHub issue context from URLs, import issues to JIRA, and bulk-link GitHub issues to JIRA tickets.' },
            { icon: <AnalyticsIcon sx={{ color: 'secondary.main' }} />, title: 'Analytics Dashboard', desc: 'Track queries, response times, cache hit rates, expert utilization, and team performance over time.' },
            { icon: <TerminalIcon sx={{ color: 'primary.main' }} />, title: 'Agent Mode', desc: 'Delegate coding tasks to an AI agent that plans, implements, and creates PRs against your team repos.' },
            { icon: <ForumIcon sx={{ color: 'info.main' }} />, title: 'Thread Conversations', desc: 'Follow-up questions in threads maintain full conversation context for more relevant, coherent responses.' },
          ].map((item) => (
            <Grid item xs={12} sm={6} key={item.title}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box sx={{ mt: 0.25 }}>{item.icon}</Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ─── Commands ────────────────────────────────────────────── */}
      {renderCategoryHeader('Commands')}

      <TextField
        size="small"
        placeholder="Search all commands..."
        value={cmdFilter}
        onChange={(e) => setCmdFilter(e.target.value)}
        sx={{ mb: 3, maxWidth: 360 }}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />

      {/* Core Commands */}
      <Box id="core-commands" ref={setRef('core-commands')} sx={{ scrollMarginTop: 80, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Core Bot Commands</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These commands are always available. Some require Team Lead role.
        </Typography>
        {renderCommandTable(filteredCore, true)}
        {filteredCore.length === 0 && noResults}
      </Box>

      {/* JIRA Commands */}
      <Box id="jira-commands" ref={setRef('jira-commands')} sx={{ scrollMarginTop: 80, mb: 4 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IntegrationIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>JIRA Commands</Typography>
              <Chip label="Optional" size="small" variant="outlined" sx={{ ml: 1 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 2 }}>
              Disabled by default. Enable in <strong>AI &amp; Prompting → Feature Settings → Enable JIRA Commands</strong>.
              Your team must also have a JIRA project key configured in Team Settings.
            </Alert>

            {filteredJiraCreate.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>Create & Manage</Typography>
                {renderCommandTable(filteredJiraCreate)}
              </>
            )}

            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Draft Workflow:</strong> When you run <Box component="code" sx={codeSx}>!jira create</Box>,
              the bot generates a draft first. Use <Box component="code" sx={codeSx}>!jira accept</Box> to
              confirm or <Box component="code" sx={codeSx}>!jira reject &lt;feedback&gt;</Box> to revise it.
            </Alert>

            {filteredJiraSearch.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Search & Filter</Typography>
                {renderCommandTable(filteredJiraSearch)}
              </>
            )}

            {filteredJiraHierarchy.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Hierarchy & Organization</Typography>
                {renderCommandTable(filteredJiraHierarchy)}
              </>
            )}

            {filteredJiraImport.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Import & Stats</Typography>
                {renderCommandTable(filteredJiraImport)}
              </>
            )}

            {cmdFilter && filteredJiraCreate.length + filteredJiraSearch.length + filteredJiraHierarchy.length + filteredJiraImport.length === 0 && noResults}

            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Note:</strong> The JIRA service account must have access to your project.
                Contact your admin for the service account username if you see permission errors.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* GitHub Commands */}
      <Box id="github-commands" ref={setRef('github-commands')} sx={{ scrollMarginTop: 80, mb: 4 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GitHubIcon />
              <Typography variant="h6" fontWeight={600}>GitHub Commands</Typography>
              <Chip label="Optional" size="small" variant="outlined" sx={{ ml: 1 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 2 }}>
              Disabled by default. Enable in <strong>AI &amp; Prompting → Feature Settings → Enable GitHub Issues</strong>.
            </Alert>
            {renderCommandTable(filteredGithub)}
            {filteredGithub.length === 0 && noResults}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              The <Box component="code" sx={codeSx}>!github link</Box> command accepts multiple lines,
              each with a GitHub issue URL and a JIRA ticket key. Duplicates are automatically skipped.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Agent Commands */}
      <Box id="agent-commands" ref={setRef('agent-commands')} sx={{ scrollMarginTop: 80, mb: 4 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TerminalIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>Agent Commands</Typography>
              <Chip label="Optional" size="small" variant="outlined" sx={{ ml: 1 }} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 2 }}>
              Disabled by default. Enable in <strong>AI &amp; Prompting → Feature Settings → Enable Agent</strong>.
              Requires an Anthropic API key configured in Agent Settings.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The Agent uses Claude Code CLI to plan, implement, and create pull requests for coding tasks.
              It auto-detects the relevant repo from your task description.
            </Typography>
            {renderCommandTable(filteredAgent)}
            {filteredAgent.length === 0 && noResults}
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Workflow</Typography>
            <Typography variant="body2" color="text.secondary">
              1. Run <Box component="code" sx={codeSx}>!agent &lt;task&gt;</Box> to create a task.
              2. If the repo is unambiguous (a URL or a distinctive name in the description
              matches exactly one repo), the task goes straight to planning — no confirmation
              needed. Otherwise the bot lists the matching repos and asks you to
              run <Box component="code" sx={codeSx}>!agent confirm &lt;id&gt; &lt;repo#&gt;</Box>.
              3. Once a repo is set, the agent generates an implementation plan.
              4. <Box component="code" sx={codeSx}>!agent approve</Box> to execute,
              or <Box component="code" sx={codeSx}>!agent reject</Box> to revise.
              5. The agent implements the plan and creates a PR when done.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* ─── Admin ───────────────────────────────────────────────── */}
      {renderCategoryHeader('Administration')}

      {/* Team Settings */}
      <Box id="team-settings" ref={setRef('team-settings')} sx={{ scrollMarginTop: 80, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Team Settings & Feature Toggles</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These settings are configured per team in the <strong>AI &amp; Prompting</strong> tab.
          Team leads can enable or disable features as needed.
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '35%' }}>Setting</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '10%' }}>Default</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { setting: 'Expert Tagging', default_: 'On', desc: 'Include @expert mentions in bot responses' },
                { setting: 'Prioritization Escalation', default_: 'On', desc: 'Suggest team lead for prioritization requests' },
                { setting: 'Multiple Thread Responses', default_: 'Off', desc: 'Bot responds to all messages in a thread (vs. first only)' },
                { setting: 'Response Caching', default_: 'Off', desc: 'Cache and reuse answers for similar queries' },
                { setting: 'Web Fetch', default_: 'On', desc: 'Fetch content from URLs shared in messages' },
                { setting: 'Proactive Docs Fetch', default_: 'On', desc: 'Fetch live content from docs and repos during context building' },
                { setting: 'JIRA Commands', default_: 'Off', desc: 'Enable !jira commands for ticket management' },
                { setting: 'GitHub Issues', default_: 'Off', desc: 'Enable GitHub issue tracking and !github commands' },
                { setting: 'Agent Mode', default_: 'Off', desc: 'Enable !agent commands for AI coding tasks' },
                { setting: 'Cache Review Context', default_: 'Off', desc: 'Include cache review entries in AI context' },
              ].map((row) => (
                <TableRow key={row.setting}>
                  <TableCell>{row.setting}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.default_}
                      size="small"
                      color={row.default_ === 'On' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{row.desc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Admin Features */}
      <Box id="admin-features" ref={setRef('admin-features')} sx={{ scrollMarginTop: 80, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Admin Features</Typography>
        <List disablePadding>
          {[
            { icon: <SecurityIcon color="warning" />, title: 'Team Approval Workflow', desc: 'New teams must be approved by admins before the bot activates in their Google Chat space' },
            { icon: <GroupIcon color="primary" />, title: 'User Management', desc: 'Manage user roles (admin, team_lead, member), control access, and view all users across teams' },
            { icon: <CachedIcon color="info" />, title: 'Cache Review', desc: 'Review, approve, edit, or delete cached bot responses to maintain answer quality' },
            { icon: <AnalyticsIcon color="secondary" />, title: 'Cross-Team Analytics', desc: 'View aggregated query volumes, response times, and cache hit rates across all teams' },
            { icon: <ChatIcon color="primary" />, title: 'Integration Management', desc: 'Configure Google Chat webhooks, Confluence connections, and global service accounts' },
            { icon: <AdminIcon color="warning" />, title: 'Application Logs', desc: 'View system logs for debugging bot behavior and monitoring errors' },
          ].map((item) => (
            <ListItem key={item.title} disablePadding sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.title}
                secondary={item.desc}
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Changelog */}
      <Box id="changelog" ref={setRef('changelog')} sx={{ scrollMarginTop: 80 }}>
        <Card>
          <CardActionArea onClick={() => navigate('/changelog')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ChangelogIcon sx={{ color: 'success.main', fontSize: 28 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>Changelog</Typography>
                  <Typography variant="body2" color="text.secondary">
                    View the latest updates, bug fixes, and new features added to AI Wingman.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </CardActionArea>
        </Card>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', gap: 3 }}>
      {isDesktop && toc}
      {content}
    </Box>
  );
}
